import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import Toggle from '../../Toggle';
import {
	Wrapper,
	Header,
	ButtonClose,
	Loader,
	NoTemplatesLabel,
} from './styles';
import HeaderLogo from '../../../containers/MenuManager/Header/Logo';
import DriveItemList from '../../DriveItemList';
import { getPublicationTemplates } from '../../../containers/PublicationCreator/actions';
import { LoadingStatus, SelectedTemplate } from '../../../containers/PublicationCreator/reducer';
import { ListDisplayType } from '../../../containers/DriveItemList/constants';
import { DriveItemInfoMap } from '../../../containers/DriveItemList/reducer';
import { Api } from '../../../typings/api';
import Utils from '../../../utils';
import useResetDrivePage from '../../../utils/customHooks/useResetDrivePage';

interface PublicationTemplatesProps {
	isDisplayed: boolean;
	loadingStatus: LoadingStatus;
	masterTemplatesIds: number[];
	customTemplatesIds: number[];
	driveItems: DriveItemInfoMap;
	onClose: () => void;
	onSelect: (item: SelectedTemplate) => void;
	dispatch: Dispatch<StoreState>;
}

enum TemplateSource {
	MASTER_TEMPLATES = 'MASTER_TEMPLATES',
	CUSTOM_TEMPLATES = 'CUSTOM_TEMPLATES',
}

const templateSourceConfig = {
	[TemplateSource.MASTER_TEMPLATES]: {
		id: TemplateSource.MASTER_TEMPLATES,
		label: 'messages.templates',
	},
	[TemplateSource.CUSTOM_TEMPLATES]: {
		id: TemplateSource.CUSTOM_TEMPLATES,
		label: 'messages.my_templates',
	},
};

const PublicationTemplates = ({
	isDisplayed,
	loadingStatus,
	masterTemplatesIds,
	customTemplatesIds,
	driveItems,
	onClose,
	onSelect,
	dispatch,
}: PublicationTemplatesProps) => {
	const [templateSource, setTemplateSource] = React.useState(TemplateSource.MASTER_TEMPLATES);
	const shouldResetDrivePage = useResetDrivePage([templateSource]);

	React.useEffect(() => {
		if (isDisplayed && loadingStatus === LoadingStatus.NOT_LOADED) {
			dispatch(getPublicationTemplates());
		}

		if (loadingStatus === LoadingStatus.LOADED && masterTemplatesIds.length === 0 && customTemplatesIds.length > 0) {
			setTemplateSource(TemplateSource.CUSTOM_TEMPLATES);
		}
	}, [isDisplayed, loadingStatus]);

	const handleTemplateSelect = ({ id, label, format }: Api.DriveItem): void => {
		onSelect({
			id,
			label,
			format,
			isCustomTemplate: templateSource === TemplateSource.CUSTOM_TEMPLATES,
		});
	};

	const hasTemplates = templateSource === TemplateSource.MASTER_TEMPLATES
		? masterTemplatesIds.length > 0
		: customTemplatesIds.length > 0;
	const templatesIdsToDisplay = templateSource === TemplateSource.MASTER_TEMPLATES
		? masterTemplatesIds
		: customTemplatesIds;

	return (
		<Wrapper>
			<Header>
				<HeaderLogo isDisabled={true} />
				<ButtonClose
					type="button"
					onClick={onClose}
				>
					<Translate value="messages.cancel" />
				</ButtonClose>
			</Header>
			{loadingStatus === LoadingStatus.LOADING &&
				<Loader>
					{Utils.getSvgIcon('iconset_loading')}
					<Translate value="messages.loading" />
				</Loader>
			}
			{loadingStatus === LoadingStatus.LOADED &&
				<>
					<Toggle
						items={Object.values(templateSourceConfig)}
						activeId={templateSource}
						onItemClick={id => setTemplateSource(id as TemplateSource)}
					/>
					{hasTemplates
						? (
							<DriveItemList
								hasBottomBar={false}
								searchConfig={undefined}
								displayType={ListDisplayType.Grid}
								storeDriveItems={driveItems}
								driveItemIdsToDisplay={templatesIdsToDisplay}
								sharedDirId={undefined}
								sourcesDirId={undefined}
								isPrivateDir={false}
								isFetching={false}
								showUnreadBadge={false}
								hasSelectionIcon={false}
								hasActions={templateSource === TemplateSource.CUSTOM_TEMPLATES}
								navigationItems={[]}
								shouldGetCustomTemplates={true}
								isEditMode={false}
								isEditButtonDisplayed={false}
								isDisplayTypeDropdownDisplayed={false}
								isLoading={false}
								isTheaterOpened={false}
								onDriveItemClick={handleTemplateSelect}
								shouldResetPage={shouldResetDrivePage}
								dispatch={dispatch}
							/>
						)
						: <NoTemplatesLabel value="messages.no_templates" />
					}
				</>
			}
		</Wrapper>
	);
};

export default PublicationTemplates;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/PublicationCreator/PublicationTemplates/index.tsx